import { handleChange } from '@helpers/handleChange';
import { tagsService } from '@services/domain-factories/tags';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { initOnce } from '@utils/zustand/initOnce';
import { Tag } from '@vincerosoft/assessfluence-types';

type TagContext = Tag['context'];
type TagsByContext = Partial<Record<TagContext, Tag[] | null>>;

type State = {
  tags: TagsByContext;
  loading: boolean;
  inFlightInit: Promise<void> | null;
};

type Action = {
  initTagsForCampaigns: (campaignIds: string[]) => Promise<void>;
  initTagsForStudio: (formIds: string[], formGroupIds: string[]) => Promise<void>;
  initTagsForEntities: () => Promise<void>;
  updateTags: (context: TagContext, tags: Tag[]) => void;
  handleChange: <T extends boolean>(
    context: TagContext,
    change: T extends true ? string : Tag,
    isDelete?: T extends true ? T : never
  ) => void;
  reset: () => void;
};

export const globalTagsStore = createStoreWithSelector<State & { resetToken: number } & Action>()(
  (set, get) => ({
    tags: {},
    loading: false,
    inFlightInit: null,
    resetToken: 0,
    initTagsForCampaigns: (campaignIds) =>
      initOnce(
        'tags.campaigns',
        set,
        get,
        () =>
          tagsService.loadTagsForCampaigns(campaignIds, (tags) => {
            set({ tags: { ...get().tags, campaigns: tags } });
          }),
        (tags) => set({ tags: { ...get().tags, campaigns: tags.flat() } })
      ),
    initTagsForStudio: (formIds, formGroupIds) =>
      initOnce(
        'tags.studio',
        set,
        get,
        () => tagsService.loadTagsForForms({ forms: formIds, formGroups: formGroupIds }),
        (tags) => set({ tags: { ...get().tags, studio: tags } })
      ),
    initTagsForEntities: () =>
      initOnce(
        'tags.entities',
        set,
        get,
        () => tagsService.loadEntityTags(),
        (tags) => set({ tags: { ...get().tags, entities: tags } })
      ),
    updateTags: (context, tags) => set((state) => ({ tags: { ...state.tags, [context]: tags } })),
    handleChange: (context, change, isDelete) => {
      set((state) => ({
        tags: {
          ...state.tags,
          [context]: handleChange(state.tags[context] ?? [], change, isDelete)
        }
      }));
    },
    reset: () => set((state) => ({ ...state, tags: {}, resetToken: (state.resetToken || 0) + 1 }))
  })
);

export const useGlobalTagsStore = createUseStore(globalTagsStore);
